/**
 * Horizon overrides for Shopify.actions:
 * - updateCart: emit events from the cart drawer scope.
 * - openCart: open the cart drawer (fall back to /cart when absent).
 */

function init() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.updateCart.configure({
    eventTarget: () => document.querySelector('theme-drawer#cart-drawer') ?? document,
  });

  actions.openCart.configure({
    async handler() {
      /** @type {HTMLElement & {open?: () => void} | null} */
      const drawer = document.querySelector('theme-drawer#cart-drawer');

      if (drawer?.open) {
        drawer.open();
      } else {
        window.location.href = Theme.routes.cart_url || '/cart';
      }
    },
  });
}

// Run immediately if the standard-actions bundle has already attached
// `Shopify.actions`; otherwise wait for DOMContentLoaded, which fires after
// all module scripts have executed regardless of document order.
if (window.Shopify?.actions) {
  init();
} else {
  document.addEventListener('DOMContentLoaded', init, { once: true });
}

/**
 * Kora fallback for Shopify's native cookie banner.
 *
 * Shopify remains the source of truth. This code only compensates for the case
 * where Shopify reports that the banner should be shown but the native banner
 * fails to open automatically. A local marker is written ONLY after Shopify
 * fires `visitorConsentCollected`, i.e. after an actual consent interaction.
 */
function initCookieBannerFallback() {
  if (window.__koraCookieBannerFallbackInitialized) return;
  window.__koraCookieBannerFallbackInitialized = true;

  const LEGACY_STORAGE_KEY = 'kora_privacy_choice';
  const STORAGE_KEY = 'kora_privacy_choice_v2';
  const STORAGE_TTL = 180 * 24 * 60 * 60 * 1000;
  const READY_POLL_MS = 250;
  const READY_MAX_ATTEMPTS = 20;

  let bannerShown = false;
  let readyTimer = null;

  // Remove the previous marker because it could have been written from inferred
  // consent state instead of a real visitor interaction.
  try {
    window.localStorage?.removeItem(LEGACY_STORAGE_KEY);
  } catch (_error) {
    // Ignore storage restrictions.
  }

  const readInteractionMarker = () => {
    try {
      const raw = window.localStorage?.getItem(STORAGE_KEY);
      if (!raw) return false;

      const parsed = JSON.parse(raw);
      const timestamp = Number(parsed?.timestamp);

      if (parsed?.value !== 'collected' || !Number.isFinite(timestamp)) {
        window.localStorage?.removeItem(STORAGE_KEY);
        return false;
      }

      if (Date.now() - timestamp >= STORAGE_TTL) {
        window.localStorage?.removeItem(STORAGE_KEY);
        return false;
      }

      return true;
    } catch (_error) {
      return false;
    }
  };

  const storeInteractionMarker = () => {
    try {
      window.localStorage?.setItem(
        STORAGE_KEY,
        JSON.stringify({ value: 'collected', timestamp: Date.now() })
      );
    } catch (_error) {
      // Storage can be unavailable in privacy-restricted browsers.
    }
  };

  // Shopify documents this event as the signal emitted when visitor consent
  // changes. The custom marker is created only from this event, never by
  // interpreting yes/no values on page load.
  document.addEventListener('visitorConsentCollected', storeInteractionMarker);

  const stopReadyPolling = () => {
    if (readyTimer !== null) {
      window.clearInterval(readyTimer);
      readyTimer = null;
    }
  };

  const evaluate = () => {
    const customerPrivacy = window.Shopify?.customerPrivacy;
    const banner = window.privacyBanner;

    if (!customerPrivacy?.shouldShowBanner || !banner?.showBanner) {
      return false;
    }

    try {
      const shouldShow = customerPrivacy.shouldShowBanner();

      // Shopify is checked first on every page. If Shopify says no banner is
      // needed, the fallback does nothing regardless of local state.
      if (!shouldShow) {
        stopReadyPolling();
        return true;
      }

      // If Shopify still reports true after a real interaction, suppress only
      // this custom fallback. The marker cannot exist for a fresh visitor unless
      // Shopify previously emitted visitorConsentCollected in this browser.
      if (readInteractionMarker()) {
        stopReadyPolling();
        return true;
      }

      if (!bannerShown) {
        bannerShown = true;
        Promise.resolve(banner.showBanner()).catch((error) => {
          console.warn('Kora cookie banner fallback: impossibile mostrare il banner.', error);
        });
      }

      stopReadyPolling();
      return true;
    } catch (error) {
      console.warn('Kora cookie banner fallback: verifica consenso non riuscita.', error);
      stopReadyPolling();
      return true;
    }
  };

  const startReadyPolling = () => {
    if (evaluate()) return;

    let attempts = 0;
    readyTimer = window.setInterval(() => {
      attempts += 1;
      if (evaluate() || attempts >= READY_MAX_ATTEMPTS) {
        stopReadyPolling();
      }
    }, READY_POLL_MS);
  };

  if (window.Shopify?.customerPrivacy) {
    startReadyPolling();
    return;
  }

  if (window.Shopify?.loadFeatures) {
    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      (error) => {
        if (error) {
          console.warn('Kora cookie banner fallback: Customer Privacy API non caricata.', error);
          return;
        }
        startReadyPolling();
      }
    );
    return;
  }

  startReadyPolling();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCookieBannerFallback, { once: true });
} else {
  initCookieBannerFallback();
}
